'use client';

import React, { useState, useCallback, useRef } from 'react';

interface ReadingModeProps {
  isActive: boolean;
  onToggle: () => void;
}

export function ReadingModeToggle({ isActive, onToggle }: ReadingModeProps) {
  return (
    <button
      onClick={onToggle}
      title={isActive ? 'Exit reading mode' : 'Enter distraction-free reading mode'}
      style={{
        position: 'fixed',
        bottom: 32,
        right: 32,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        background: isActive ? 'rgba(201,168,76,0.15)' : 'rgba(6,13,18,0.92)',
        border: `1px solid ${isActive ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.2)'}`,
        borderRadius: 6,
        color: isActive ? '#c9a84c' : 'rgba(212,197,169,0.6)',
        fontFamily: 'monospace',
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        backdropFilter: 'blur(12px)',
        transition: 'all 0.2s',
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
      }}
    >
      <span style={{ fontSize: 14 }}>{isActive ? '⊠' : '⊡'}</span>
      {isActive ? 'Exit Reading' : 'Reading Mode'}
    </button>
  );
}

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────

export function exportLetterAsPDF() {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  // Collect all letter text content from the page
  const letterSections = document.querySelectorAll(
    '#section-opening, #section-caretaker, #section-the-mural, #section-roots-marifah, ' +
    '#section-al-qadir, #section-aql, #section-ilm, #section-hawas, #section-al-alim-al-hakim, '+ '#section-entering-haal, #section-islah, #section-ar-rahman-compassion, #section-hadith-jibril, '+ '#section-al-haseeb-ar-raqib, #section-till-rows, #section-rope-of-language, '+ '#section-amanah-trust, #section-haqq, #section-al-khaliq, '+ '#section-al-khabir-al-zahir, #section-examination, #section-steppin, #section-branding-closing'
  );

  let bodyHTML = '';
  letterSections.forEach(section => {
    // Extract only text-bearing elements, skip interactive UI
    const clone = section.cloneNode(true) as HTMLElement;
    // Remove buttons, nav, interactive elements
    clone.querySelectorAll('button, nav, [role="tablist"], .font-mono[class*="tracking-widest"][class*="uppercase"]:not(p)').forEach(el => {
      // Keep section labels and root labels but remove interactive controls
      if ((el as HTMLElement).tagName === 'BUTTON') el.remove();
    });
    bodyHTML += `<div class="letter-section">${clone.innerHTML}</div>`;
  });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>The Haal Project — Open Letter</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Amiri:ital,wght@0,400;0,700;1,400&family=Source+Serif+4:ital,opsz,wght@0,8..60,300..700;1,8..60,300..700&display=swap');
    
    * { box-sizing: border-box; margin: 0; padding: 0; }
    
    body {
      font-family: 'Source Serif 4', Georgia, serif;
      font-size: 12pt;
      line-height: 1.9;
      color: #1a1a1a;
      background: #fff;
      max-width: 680px;
      margin: 0 auto;
      padding: 48pt 48pt;
    }
    
    h1, h2, h3 {
      font-family: 'Source Serif 4', Georgia, serif;
      font-weight: 700;
      margin-bottom: 12pt;
      margin-top: 24pt;
      color: #111;
    }
    
    h2 { font-size: 18pt; }
    h3 { font-size: 14pt; }
    
    p {
      margin-bottom: 10pt;
      color: #222;
    }
    
    em, i { color: #333; }
    
    .letter-section {
      margin-bottom: 32pt;
      page-break-inside: avoid;
    }
    
    /* Quranic verses */
    [style*="fontStyle: italic"], i, em {
      font-style: italic;
    }
    
    /* Arabic text */
    [dir="rtl"], [style*="direction: rtl"] {
      font-family: 'Amiri', serif;
      font-size: 16pt;
      direction: rtl;
      text-align: right;
      display: block;
      margin: 8pt 0;
      color: #555;
    }
    
    /* Root analysis tags — render as inline list */
    span[style*="background"] {
      display: inline;
      background: none !important;
      border: none !important;
      padding: 0 !important;
      color: #333 !important;
    }
    span[style*="background"]:not(:last-child)::after {
      content: " · ";
      color: #999;
    }
    
    /* Verse/hadith blocks */
    div[style*="borderLeft"] {
      border-left: 2pt solid #999 !important;
      padding-left: 12pt !important;
      margin: 12pt 0 !important;
      background: none !important;
    }
    
    /* Section labels */
    p[style*="tracking-widest"], .font-mono {
      font-family: 'Courier New', monospace;
      font-size: 8pt;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #888;
    }
    
    /* Hide interactive UI elements */
    button, nav, [role="tablist"], [role="tab"],
    a[href]:not([href^="#"]) { display: none !important; }
    
    @page {
      margin: 48pt;
      size: letter;
    }
    
    @media print {
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <div style="text-align:center; margin-bottom: 48pt; padding-bottom: 24pt; border-bottom: 1pt solid #ccc;">
    <h1 style="font-size:24pt; margin-bottom:8pt;">The Haal Project</h1>
    <p style="font-size:11pt; color:#666; font-style:italic;">Open Letter — Distraction-Free Export</p>
    <p style="font-size:9pt; color:#999; margin-top:8pt;">${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
  </div>
  ${bodyHTML}
</body>
</html>`;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
}

// ─── READING MODE OVERLAY ─────────────────────────────────────────────────────

export default function ReadingModeWrapper({ children }: { children: React.ReactNode }) {
  const [readingMode, setReadingMode] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const toggleReadingMode = useCallback(() => {
    setReadingMode(prev => {
      const next = !prev;
      if (next) {
        document.body.style.overflow = 'auto';
      }
      return next;
    });
    setShowExportMenu(false);
  }, []);

  return (
    <>
      {/* Reading mode styles injected globally */}
      {readingMode && (
        <style>{`
          /* Hide all chrome in reading mode */
          header, footer, nav,
          .plot3-sidebar-trail,
          .letter-mobile-section-nav,
          [class*="LetterProgressBar"],
          #section-plot5,
          #section-role-publishing,
          #section-hikmah-layer-sync,
          [id*="synthesis"], [id*="vocational"], [id*="quest"],
          .letter-bridges-section,
          /* Hide Platform Bridge callout boxes */
          div[style*="Platform Bridge"],
          /* Hide IRL Lens Connection boxes */
          div[style*="IRL Lens Connection"],
          /* Hide tab selectors */
          div[style*="activeTab"],
          /* Hide section labels (◈ Plot 04 · Section) */
          p.font-mono[style*="rgba(139,105,20,0.6)"],
          /* Hide annotation panels */
          .letter-annotation-panel,
          .letter-mobile-annotation-sheet
          {
            display: none !important;
          }

          /* Reading mode typography */
          .letter-main-content {
            padding-right: 0 !important;
            max-width: 680px !important;
            margin: 0 auto !important;
          }

          /* Clean background */
          body {
            background: #0a0f14 !important;
          }

          /* Increase body text size for reading */
          .letter-body-text,
          [style*="fontSize: 15"][style*="lineHeight: 1.9"],
          [style*="fontSize: 14"][style*="lineHeight: 1.85"] {
            font-size: 16px !important;
            line-height: 2 !important;
          }

          /* Reduce section padding */
          section {
            padding: 48px 0 !important;
          }

          /* Hide interactive tabs in Entering Haal Platform */
          div[style*="flex gap-2 mb-6"] {
            display: none !important;
          }

          /* Hide root meaning tag clouds — show as plain text instead */
          div[style*="flex flex-wrap gap-2"] span {
            background: none !important;
            border: none !important;
            padding: 0 !important;
          }
          div[style*="flex flex-wrap gap-2"] span:not(:last-child)::after {
            content: " · ";
            color: rgba(201,168,76,0.3);
          }

          /* Simplify verse blocks */
          div[style*="borderLeft: '3px solid"] {
            border-left: 2px solid rgba(201,168,76,0.3) !important;
            background: transparent !important;
          }

          /* Hide section number labels */
          p[style*="◈ Plot"] {
            display: none !important;
          }
        `}</style>
      )}

      {children}

      {/* Reading mode toggle button */}
      <ReadingModeToggle isActive={readingMode} onToggle={toggleReadingMode} />

      {/* PDF Export button — always visible, more prominent in reading mode */}
      <button
        onClick={() => setShowExportMenu(prev => !prev)}
        title="Export letter as PDF"
        style={{
          position: 'fixed',
          bottom: 32,
          right: readingMode ? 200 : 200,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          background: 'rgba(6,13,18,0.92)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: 6,
          color: 'rgba(212,197,169,0.6)',
          fontFamily: 'monospace',
          fontSize: 10,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          cursor: 'pointer',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.2s',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        }}
      >
        <span style={{ fontSize: 14 }}>⬇</span>
        Export PDF
      </button>

      {/* Export menu */}
      {showExportMenu && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            right: 200,
            zIndex: 9999,
            background: 'rgba(6,13,18,0.97)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: 16,
            minWidth: 220,
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            backdropFilter: 'blur(16px)',
          }}
        >
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 12 }}>
            Export Options
          </p>
          <button
            onClick={() => { exportLetterAsPDF(); setShowExportMenu(false); }}
            style={{
              display: 'block',
              width: '100%',
              padding: '10px 14px',
              background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.2)',
              borderRadius: 4,
              color: '#c9a84c',
              fontFamily: 'monospace',
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              textAlign: 'left',
              marginBottom: 8,
            }}
          >
            ⬇ Print / Save as PDF
          </button>
          <p style={{ fontSize: 10, color: 'rgba(212,197,169,0.35)', lineHeight: 1.5 }}>
            Opens a print-ready version of the letter. Use your browser's "Save as PDF" option.
          </p>
          <button
            onClick={() => setShowExportMenu(false)}
            style={{
              marginTop: 12,
              background: 'none',
              border: 'none',
              color: 'rgba(212,197,169,0.3)',
              fontFamily: 'monospace',
              fontSize: 9,
              cursor: 'pointer',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            ✕ Close
          </button>
        </div>
      )}
    </>
  );
}
